import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Download, Trash2, Upload, Volume2, AlertCircle,
  MapPin, Clock, FileText, Camera, Image as ImageIcon, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Severity palette kept in sync with Incidents list
const severityVariant = (sev) => {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return 'destructive';
    case 'major':    return 'destructive';
    case 'minor':    return 'secondary';
    default:         return 'outline';
  }
};

const IncidentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [retranscribing, setRetranscribing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    fetchAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchIncident(), fetchAudioUrl(), fetchPhotoUrls()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncident = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Not found');
      const data = await resp.json();
      setIncident(data);
    } catch (err) {
      console.error('fetchIncident error:', err);
      toast.error('Incident not found');
      navigate('/app/incidents', { replace: true });
    }
  };

  const fetchAudioUrl = async () => {
    setAudioLoading(true);
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}/audio-url`, { credentials: 'include' });
      if (resp.status === 404) {
        setAudioUrl(null);
        return;
      }
      if (!resp.ok) throw new Error('Failed to get audio URL');
      const data = await resp.json();
      // Presigned IONOS URLs are absolute; backend-stream fallback is relative
      const finalUrl = data.url?.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
      setAudioUrl(finalUrl);
    } catch (err) {
      console.warn('Audio URL fetch failed:', err);
      setAudioUrl(null);
    } finally {
      setAudioLoading(false);
    }
  };

  const fetchPhotoUrls = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}/photo-urls`, { credentials: 'include' });
      if (!resp.ok) { setPhotos([]); return; }
      const data = await resp.json();
      setPhotos(data.photos || []);
    } catch (err) {
      console.warn('Photo URL fetch failed:', err);
      setPhotos([]);
    }
  };

  const handleRetranscribe = async () => {
    if (retranscribing) return;
    setRetranscribing(true);
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}/transcribe`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Transcription failed');
      const data = await resp.json();
      setIncident((prev) => ({ ...prev, transcription: data.transcription, transcription_confidence: data.confidence }));
      toast.success('Transcription updated');
    } catch (err) {
      console.error(err);
      toast.error('Transcription failed — check backend logs');
    } finally {
      setRetranscribing(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${backendUrl}/api/incidents/${id}/photos`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!resp.ok) throw new Error('Upload failed');
      toast.success('Photo added');
      await fetchPhotoUrls();
      await fetchIncident();
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this incident? This cannot be undone.')) return;
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');
      toast.success('Incident deleted');
      navigate('/app/incidents', { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete incident');
    }
  };

  const handleExportPdf = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/incidents/${id}/export/pdf`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('PDF export failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incident-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="incident-detail-loading">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!incident) return null;

  const formattedTime = incident.timestamp
    ? new Date(incident.timestamp).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  const confidencePct = typeof incident.transcription_confidence === 'number'
    ? Math.round(incident.transcription_confidence * 100)
    : null;

  const hasLocation = incident.location?.lat && incident.location?.lng;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6"
      data-testid="incident-detail-page"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/incidents')}
            data-testid="incident-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="incident-detail-title">
              {incident.title || 'Incident'}
            </h1>
            <p className="text-sm text-muted-foreground">ID: {incident.incident_id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="incident-detail-export-pdf">
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="incident-detail-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Summary card */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={severityVariant(incident.severity)} data-testid="incident-detail-severity">
              {incident.severity || 'unknown'}
            </Badge>
            <Badge variant="outline">{incident.incident_type || 'incident'}</Badge>
            <Badge variant="outline">{incident.status || 'recording'}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{formattedTime}</span>
            </div>
            {hasLocation && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <a
                  className="underline underline-offset-2 hover:text-primary"
                  href={`https://www.google.com/maps?q=${incident.location.lat},${incident.location.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="incident-detail-map-link"
                >
                  {incident.location.lat.toFixed(5)}, {incident.location.lng.toFixed(5)}
                </a>
              </div>
            )}
          </div>
          {incident.description && (
            <Textarea
              value={incident.description}
              readOnly
              className="resize-none"
              data-testid="incident-detail-description"
            />
          )}
        </CardContent>
      </Card>

      {/* Audio player */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-primary" />
              <h2 className="font-medium">Audio recording</h2>
            </div>
            {audioUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAudioUrl}
                disabled={audioLoading}
                data-testid="incident-detail-audio-refresh"
                title="Refresh signed URL"
              >
                <RefreshCw className={`w-4 h-4 ${audioLoading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          {audioLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : audioUrl ? (
            <audio
              src={audioUrl}
              controls
              className="w-full"
              preload="metadata"
              data-testid="incident-detail-audio-player"
            />
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" /> No audio recording attached
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transcription */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <h2 className="font-medium">Transcription</h2>
              {confidencePct !== null && (
                <Badge variant="secondary" className="ml-1" data-testid="incident-detail-confidence">
                  {confidencePct}% confidence
                </Badge>
              )}
            </div>
            {incident.audio_url && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetranscribe}
                disabled={retranscribing}
                data-testid="incident-detail-retranscribe"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${retranscribing ? 'animate-spin' : ''}`} />
                {retranscribing ? 'Transcribing…' : 'Re-transcribe'}
              </Button>
            )}
          </div>
          {incident.transcription ? (
            <p
              className="whitespace-pre-wrap text-sm leading-relaxed"
              data-testid="incident-detail-transcript"
            >
              {incident.transcription}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No transcription yet. {incident.audio_url ? 'Click Re-transcribe to generate one.' : 'Upload audio to transcribe.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-primary" />
              <h2 className="font-medium">Photos ({photos.length})</h2>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="incident-detail-photo-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="incident-detail-add-photo"
              >
                <Upload className="w-4 h-4 mr-1" />
                {uploadingPhoto ? 'Uploading…' : 'Add photo'}
              </Button>
            </div>
          </div>

          {photos.length === 0 ? (
            <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <ImageIcon className="w-6 h-6" />
              No photos attached yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, idx) => (
                <a
                  key={`${p.path}-${idx}`}
                  href={p.signed_url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="block group relative overflow-hidden rounded-md border bg-muted aspect-square"
                  data-testid={`incident-detail-photo-${idx}`}
                >
                  {p.signed_url ? (
                    <img
                      src={p.signed_url}
                      alt={p.caption || `Incident photo ${idx + 1}`}
                      className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                      Unavailable
                    </div>
                  )}
                  {p.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                      {p.caption}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Related incident quick-links */}
      <div className="text-center">
        <Link
          to="/app/incidents/record"
          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          data-testid="incident-detail-record-new"
        >
          <AlertCircle className="w-4 h-4" /> Record another incident
        </Link>
      </div>
    </motion.div>
  );
};

export default IncidentDetail;
